import {Component, inject, Inject, Optional} from '@angular/core';
import {IngredientService} from '../../services/ingredient.service';
import {MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle} from '@angular/material/dialog';
import {MatCheckbox, MatCheckboxChange} from '@angular/material/checkbox';
import {THEME_CONFIG} from '../../../environments/tokens/theme.token';
import {MatButton} from '@angular/material/button';
import {CdkScrollable} from '@angular/cdk/scrolling';
import {MatButtonToggle, MatButtonToggleGroup} from '@angular/material/button-toggle';

export interface FilterDialogData {
  selectedIngredients: string[];
  selectedAllergens: string[];
}

@Component({
  selector: 'app-product-filter-dialog',
  templateUrl: './product-filter-dialog.component.html',
  styleUrl: './product-filter-dialog.component.scss',
  standalone: true,
  imports: [MatDialogTitle, MatButton, CdkScrollable, MatDialogContent, MatCheckbox, MatButtonToggleGroup, MatButtonToggle]
})
export class ProductFilterDialogComponent {
  mode: 'flags' | 'ingredients' = 'flags';
  items: any[] = [];
  loading = true;
  private savedFlags: Map<string, { selected: boolean; sign: string; label: string }> = new Map();
  private savedIngredients: Map<string, { selected: boolean; sign: string; label: string }> = new Map();
  private flagsData: Map<string, string> = new Map();
  private ingredientsData: Map<string, string> = new Map();

  protected readonly themeConfig = inject(THEME_CONFIG)

  constructor(
    private dialogRef: MatDialogRef<ProductFilterDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private ingredientService: IngredientService
  ) {
    this.preloadFromActiveFilters();
    this.loadData();
  }

  private preloadFromActiveFilters() {
    const flags: string[] = this.data?.activeIngredientFlags || [];
    const ingredients: string[] = this.data?.activeIngredients || [];
    const plusLabels: string[] = this.data?.plusLabels || [];
    const minusLabels: string[] = this.data?.minusLabels || [];

    // Construire une map id → label depuis les labels passés
    const allIds = [
      ...flags.map(f => f.replace(/^[+-]/, '')),
      ...ingredients.map(i => i.replace(/^[+-]/, ''))
    ];
    const allLabels = [...plusLabels, ...minusLabels];

    flags.forEach(f => {
      const sign = f.startsWith('-') ? '-' : '+';
      const key = f.replace(/^[+-]/, '');
      const idx = allIds.indexOf(key);
      const label = idx !== -1 ? allLabels[idx] : key;
      this.savedFlags.set(key, {selected: true, sign, label});
    });

    ingredients.forEach(i => {
      const sign = i.startsWith('-') ? '-' : '+';
      const key = i.replace(/^[+-]/, '');
      const idx = allIds.indexOf(key);
      const label = idx !== -1 ? allLabels[idx] : key;
      this.savedIngredients.set(key, {selected: true, sign, label});
    });
  }

  switchMode(newMode: 'flags' | 'ingredients') {
    if (this.mode === newMode) return;

    // Save current tab items state WITHOUT clearing saved entries for other items
    const currentMap = this.mode === 'flags' ? this.savedFlags : this.savedIngredients;
    const labelMap = this.mode === 'flags' ? this.flagsData : this.ingredientsData;

    this.items.forEach(item => {
      if (item.selected) {
        currentMap.set(item.id, {
          selected: true,
          sign: item.sign,
          label: labelMap.get(item.id) || item.name || item.id
        });
      } else {
        currentMap.delete(item.id);
      }
    });

    this.mode = newMode;
    this.loadData();
  }
  private loadData() {
    this.loading = true;
    this.items = [];

    if (this.mode === 'flags') {
      this.ingredientService.getPublicIngredientFlags().subscribe({
        next: (res) => {
          const data = res.body?.data || {};
          this.flagsData.clear();

          this.items = Object.entries(data).map(([key, value]: [string, any]) => {
            this.flagsData.set(key, value.name || key);
            // ← Restaurer depuis savedFlags directement
            const saved = this.savedFlags.get(key);
            return {
              id: key,
              name: value.name || key,
              selected: saved ? saved.selected : false,
              sign: saved ? saved.sign : '+'
            };
          });

          this.loading = false;
        },
        error: () => {
          this.loading = false;
        }
      });

    } else {
      this.ingredientService.getPublicIngredients().subscribe({
        next: (res) => {
          const itemsArray = res.body?.data?.items || [];

          this.items = itemsArray.map((it: any) => {
            this.ingredientsData.set(it.id, it.name || 'Inconnu');
            // ← Restaurer depuis savedIngredients directement
            const saved = this.savedIngredients.get(it.id);
            return {
              id: it.id,
              name: it.name || 'Inconnu',
              selected: saved ? saved.selected : false,
              sign: saved ? saved.sign : '+'
            };
          });

          this.loading = false;
        },
        error: () => {
          this.loading = false;
        }
      });
    }
  }

  onCheckboxChange(item: any, event: MatCheckboxChange) {
    item.selected = event.checked;
    if (!event.checked) {
      item.sign = '+';
    }
  }

  onApply() {
    // 1. Sync current visible tab items into saved map
    const currentMap = this.mode === 'flags' ? this.savedFlags : this.savedIngredients;
    const labelMap = this.mode === 'flags' ? this.flagsData : this.ingredientsData;

    this.items.forEach(item => {
      if (item.selected) {
        currentMap.set(item.id, {
          selected: true,
          sign: item.sign,
          label: labelMap.get(item.id) || item.name || item.id
        });
      } else {
        currentMap.delete(item.id);
      }
    });

    // 2. Collect results from BOTH savedFlags AND savedIngredients
    const flagsPlus: string[] = [];
    const flagsMinus: string[] = [];
    const flagsPlusLabels: string[] = [];
    const flagsMinusLabels: string[] = [];

    const ingredientsPlus: string[] = [];
    const ingredientsMinus: string[] = [];
    const ingredientsPlusLabels: string[] = [];
    const ingredientsMinusLabels: string[] = [];

    this.savedFlags.forEach((val, key) => {
      if (!val.selected) return;
      const label = val.label || this.flagsData.get(key) || key;
      if (val.sign === '+') {
        flagsPlus.push(key);
        flagsPlusLabels.push(label);
      } else {
        flagsMinus.push(key);
        flagsMinusLabels.push(label);
      }
    });

    this.savedIngredients.forEach((val, key) => {
      if (!val.selected) return;
      const label = val.label || this.ingredientsData.get(key) || key;
      if (val.sign === '+') {
        ingredientsPlus.push(key);
        ingredientsPlusLabels.push(label);
      } else {
        ingredientsMinus.push(key);
        ingredientsMinusLabels.push(label);
      }
    });

    this.dialogRef.close({
      flagsPlus,
      flagsMinus,
      ingredientsPlus,
      ingredientsMinus,
      plusLabels: [...flagsPlusLabels, ...ingredientsPlusLabels],
      minusLabels: [...flagsMinusLabels, ...ingredientsMinusLabels]
    });
  }
  onCancel() {
    this.dialogRef.close({ clear: true });
  }

}
